/**
 * Rule-based Email Summarizer & Action Item Extractor Utility
 * Generates executive 3-line rule-based summaries and Action Items for KakaoTalk notifications & AI Briefing Cards
 */

export const generateEmailSummary = (email = {}) => {
  if (!email) return { summaryText: '', bullets: [] };

  const subject = (email.Subject || '제목 없음').trim();
  const sender = (email.Sender || '발신자 미상').trim();
  
  // Clean raw body text
  let rawText = (email.BodyText || email.Body || email.BodyHTML || '')
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]*>/g, ' ') // Strip HTML tags
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();

  // Strip signature or footer boilerplate
  rawText = rawText
    .replace(/주식회사\s*클로우플레이크.*/gi, '')
    .replace(/Clouflake\s*AX.*/gi, '')
    .replace(/감사합니다\..*/gi, '')
    .replace(/수고하세요\..*/gi, '')
    .replace(/좋은\s*하루\s*되세요\..*/gi, '')
    .trim();

  // Split into sentences
  const rawSentences = rawText
    .split(/(?<=[.?!])\s+|\n+/)
    .map(s => s.trim())
    .filter(s => s.length > 3 && !s.startsWith('http://') && !s.startsWith('https://'));

  // Filter out pure greeting sentences to reach core agenda
  const contentSentences = rawSentences.filter(s => {
    const isGreeting = /^(안녕하세요|안녕하십니까|반갑습니다|수고\s*많으십니다|좋은\s*아침|대표님|이사님|팀장님|님,)/i.test(s);
    return !isGreeting || s.length > 35;
  });

  const sentences = contentSentences.length > 0 ? contentSentences : rawSentences;

  let agenda = '';
  let actionItem = '';
  let deadline = '';

  // 1. Identify Agenda (주요 안건)
  if (sentences.length > 0) {
    agenda = sentences[0];
    if (agenda.length > 85) agenda = agenda.substring(0, 82) + '...';
  } else {
    agenda = `${subject} 관련 업무 내용 공유 및 협조 요청`;
  }

  // 2. Identify Action Item (조치 및 요청 사항)
  const actionSentence = sentences.find(s => 
    s.includes('요청') || s.includes('부탁') || s.includes('검토') || 
    s.includes('확인') || s.includes('제출') || s.includes('회신') ||
    s.includes('진행') || s.includes('필요') || s.includes('피드백') || s.includes('공유')
  );
  if (actionSentence && actionSentence !== agenda) {
    actionItem = actionSentence.length > 85 ? actionSentence.substring(0, 82) + '...' : actionSentence;
  } else if (sentences.length > 1 && sentences[1] !== agenda) {
    actionItem = sentences[1].length > 85 ? sentences[1].substring(0, 82) + '...' : sentences[1];
  } else {
    actionItem = '상세 내용 및 첨부 문서 확인 후 필요 시 담당자 회신 요망';
  }

  // 3. Identify Deadline or Schedule (기한 및 일정)
  const dateSentence = sentences.find(s => 
    (s.includes('일정') || s.includes('까지') || s.includes('예정') || 
     s.includes('마감') || s.includes('시간') || /\d{1,2}월|\d{1,2}일|\d{1,2}시|\d{1,2}:\d{2}/.test(s)) &&
    s !== agenda && s !== actionItem
  );
  if (dateSentence) {
    deadline = dateSentence.length > 85 ? dateSentence.substring(0, 82) + '...' : dateSentence;
  } else if (sentences.length > 2 && sentences[2] !== agenda && sentences[2] !== actionItem) {
    deadline = sentences[2].length > 85 ? sentences[2].substring(0, 82) + '...' : sentences[2];
  } else {
    deadline = '업무 기한 내 확인 및 포털 웹메일 본문 열람 권장';
  }

  const bullets = [
    `1. ${agenda}`,
    `2. ${actionItem}`,
    `3. ${deadline}`
  ];

  const summaryText = bullets.join('\n');

  return {
    subject,
    sender,
    agenda,
    actionItem,
    deadline,
    bullets,
    summaryText
  };
};
